import * as React from 'react';
import get from 'lodash/get';
import styled from '@emotion/styled';
import { COLOR, FONT_WEIGHT, BREAKPOINTS } from '@ordermentum/ui-constants';

type OnClick = (e: React.MouseEvent<HTMLButtonElement>) => void;
type ButtonColor = 'white' | 'orange' | 'gray' | 'gray_charcoal' | 'green';

type StyleProps = {
  color?: ButtonColor;
  onClick?: OnClick;
  children: React.ReactNode;
  disabled?: boolean;
  bgColor?: ButtonColor;
};

const foregroundColor = {
  orange: COLOR.BRAND_500,
  gray_charcoal: COLOR.GRAY_700,
  gray: COLOR.GRAY_400,
  white: COLOR.WHITE,
  green: COLOR.FLOURESCENT_GREEN,
};

const textColor = {
  orange: COLOR.BRAND_500,
  gray_charcoal: COLOR.GRAY_700,
  gray: COLOR.GRAY_700,
  white: COLOR.WHITE,
  green: COLOR.FLOURESCENT_GREEN,
};

const getFgColor = (p: StyleProps) =>
  get(foregroundColor, p.color || 'white', 'none');

const getTextColor = (p: StyleProps) =>
  get(textColor, p.color || 'white', 'none');

const backgroundColor = {
  orange: COLOR.WHITE,
  gray: COLOR.WHITE,
  white: 'none',
};

const getBgColor = (p: StyleProps) =>
  get(backgroundColor, (p.bgColor ?? p.color) || 'white', 'none');

const StyledButton = styled('button')<StyleProps>`
  align-items: center;
  background: ${getBgColor};
  border: 1px solid #fff;
  border-color: ${getFgColor};
  border-radius: 2px;
  color: ${getTextColor};
  display: flex;
  flex-direction: row;
  font-size: 0.75rem;
  font-weight: ${FONT_WEIGHT.MEDIUM};
  line-height: 1;
  min-height: 0;
  padding: 0.5rem 0.5rem;

  [data-id='icon'] {
    margin-right: 0.5rem;
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.6;
  }

  > * {
    color: ${getTextColor};
  }

  @media screen and (max-width: ${BREAKPOINTS.MOBILE_MAX}) {
    font-size: 0.5rem;
  }
`;

interface ButtonProp {
  color?: ButtonColor;
  title?: string;
  onClick?: OnClick;
  children: React.ReactNode;
  isInactive?: boolean;
  bgColor?: ButtonColor;
  type?: 'button' | 'reset' | 'submit' | undefined;
}

/**
 * @deprecated Use @ordermentum/ui-shared/components/Button
 */
const TinyButton = ({
  children,
  color,
  onClick,
  isInactive,
  title,
  bgColor,
  type = 'button',
}: ButtonProp) => (
  <StyledButton
    color={color}
    onClick={onClick}
    disabled={isInactive}
    title={title}
    type={type}
    bgColor={bgColor}
  >
    {children}
  </StyledButton>
);

export default TinyButton;
